import { Grid } from '../../core/Grid.js';
import {
    Position,
    Direction,
    Edge,
    positionKey,
    parsePositionKey,
    edgeKey,
    edgeAdjacentCells
} from '../../core/GridGeometry.js';
import { OrbitsGame } from './OrbitsGame.js';
import { ScoreBreakdown, calculateScore } from '../../core/Scoring.js';
import { VortexGateManager } from '../VortexGateManager.js';
import { IVortexGame } from '../IVortexGame.js';

export class OrbitsVortex extends OrbitsGame implements IVortexGame {
    private readonly gm = new VortexGateManager();

    constructor(grid: Grid) {
        super(grid);
    }

    // ── Vortex Data ───────────────────────────────────────────────

    setVortexData(gatePairs: Array<[Position, Position]>, allGates: Set<string>): void {
        this.gm.setVortexData(gatePairs, allGates, () => {
            this.recalculatePath();
        });
    }

    // ── Gate Queries ──────────────────────────────────────────────

    isGateCell(pos: Position): boolean {
        return this.gm.isGateCell(pos);
    }

    isActiveGate(pos: Position): boolean {
        return this.gm.isActiveGate(pos);
    }

    canActivateGate(pos: Position): boolean {
        return this.gm.canActivateGate(pos, (p, paired) => {
            if (this.getGrid().hasDigitAt(p)) return false;
            if (this.getGrid().hasDigitAt(paired)) return false;
            if (this.hasOrbitGateOnEdges(p) || this.hasOrbitGateOnEdges(paired)) return false;
            return true;
        });
    }

    /** Check if any orbit gate edge is adjacent to the given cell */
    private hasOrbitGateOnEdges(pos: Position): boolean {
        const grid = this.getGrid();
        const gates = this.getGates();
        const directions: Direction[] = ['N', 'E', 'S', 'W'];
        for (const dir of directions) {
            const edge = grid.getEdgeFromPosition(pos, dir);
            if (gates.has(edgeKey(edge))) return true;
        }
        return false;
    }

    toggleVortexGate(pos: Position): boolean {
        return this.gm.toggleVortexGate(pos, this.canActivateGate(pos), () => {
            this.recalculatePath();
        });
    }

    getActiveGatePairs(): Map<string, string> {
        return this.gm.getActiveGatePairs();
    }

    getVortexGatePairs(): Array<[Position, Position]> {
        return this.gm.getVortexGatePairs();
    }

    getAllGatePositions(): Set<string> {
        return this.gm.getAllGatePositions();
    }

    // ── Scoring ────────────────────────────────────────────────────

    override getScore(): ScoreBreakdown | null {
        if (!this.checkWin()) return null;

        const gateCount = this.getGates().size;

        const emptyCells = new Set<string>();
        for (const pos of this.getShipPath()) {
            const key = positionKey(pos);
            if (!this.getGrid().hasDigitAt(pos)) {
                emptyCells.add(key);
            }
        }

        return calculateScore(gateCount, this.gm.deduplicateCellCount(emptyCells));
    }

    // ── Placement Blocking ────────────────────────────────────────

    override toggleGate(edge: Edge): boolean {
        // Block orbit gates on edges of active vortex gate cells
        const adjacentCells = edgeAdjacentCells(edge);
        if (adjacentCells.some(c => this.gm.isActiveGate(c))) {
            return false;
        }
        return super.toggleGate(edge);
    }

    // ── Teleportation ─────────────────────────────────────────────

    protected override onShipEntersCell(
        cell: Position, _direction: Direction, justTeleported: boolean
    ): { cell: Position; teleported: boolean } {
        if (!this.gm?.hasActiveGates() || justTeleported) {
            return { cell, teleported: false };
        }

        const key = positionKey(cell);
        const pairedKey = this.gm.raw.get(key);
        if (pairedKey) {
            return { cell: parsePositionKey(pairedKey), teleported: true };
        }
        return { cell, teleported: false };
    }

    // ── State Management ──────────────────────────────────────────

    override reset(): void {
        super.reset();
        this.gm.clearActiveGates();
    }

    override isStarted(): boolean {
        return super.isStarted() || this.gm.hasActiveGates();
    }

    // ── Serialization ─────────────────────────────────────────────

    override serialize(): string {
        return this.gm.serializeWithBase(super.serialize(), this.gs);
    }

    override deserialize(serialized: string): void {
        this.gm.clearActiveGates();
        if (!serialized) {
            super.deserialize('');
            return;
        }

        const { basePart, gatePart } = VortexGateManager.splitSerialized(serialized);
        super.deserialize(basePart);
        this.gm.deserializeGates(gatePart, this.gs);
        this.recalculatePath();
    }
}
